import { useCallback } from "react";
import CsvImporter from "@/components/CsvImporter";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { inviteUser, listSitesLite, listClients } from "@/lib/api";

/**
 * /admin/import/users — bulk-invite users via CSV.
 *
 * One Supabase Auth invite per row via the existing /api/users/invite
 * FastAPI route. `site_code` resolves to lbs_sites.id within the active
 * tenant; unmapped codes downgrade the row to a warning (user is still
 * invited at client scope, just without a site assignment).
 */

const COLUMNS = [
  { key: "email",      label: "Email",     required: true,
    description: "Primary login + invite address.", example: "alice@example.com" },
  { key: "role",       label: "Role",      required: true,
    description: "One of: super_admin, client_admin, site_admin, dock_manager, receiving, dispatch, gate, approved_booker, driver.",
    example: "dispatch" },
  { key: "site_code",  label: "Site code",
    description: "Optional. Matches lbs_sites.code within the active tenant. Leave blank for client-wide roles.",
    example: "MEL01" },
  { key: "is_primary", label: "Primary?",
    description: "true/false. Marks this assignment as the user's primary scope. Defaults to false.",
    example: "true" },
];

const VALID_ROLES = new Set([
  "super_admin", "client_admin", "site_admin", "dock_manager",
  "receiving", "dispatch", "gate", "approved_booker", "driver",
]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AdminImportUsers() {
  const { assignments, hasRole } = useAuth();
  const { viewAsClientId, isSuperAdmin } = useViewAsClient();

  // Resolve the target tenant: super_admin must select via the picker;
  // everyone else inherits from their first assignment.
  const targetClientId =
    isSuperAdmin ? (viewAsClientId || null)
                 : (assignments?.[0]?.client_id || null);

  const loadContext = useCallback(async () => {
    if (!targetClientId) {
      throw new Error(isSuperAdmin
        ? "Super admin: please pick a client in the top bar first."
        : "Your account has no tenant assignment.");
    }
    const [sitesRes, clientsRes] = await Promise.all([
      listSitesLite(targetClientId),
      isSuperAdmin ? listClients() : Promise.resolve({ data: [] }),
    ]);
    const sitesByCode = new Map();
    (sitesRes.data || []).forEach((s) => { if (s.code) sitesByCode.set(s.code.toLowerCase(), s); });
    return { clientId: targetClientId, sitesByCode, clients: clientsRes.data || [] };
  }, [targetClientId, isSuperAdmin]);

  const validateRow = (raw, ctx) => {
    const errors = [];
    const email = (raw.email || "").trim().toLowerCase();
    const role  = (raw.role  || "").trim().toLowerCase();
    const siteCode = (raw.site_code || "").trim();
    const isPrimary = ["true","1","yes","y"].includes((raw.is_primary || "").trim().toLowerCase());

    if (!email) errors.push("email required");
    else if (!EMAIL_RE.test(email)) errors.push("invalid email");
    if (!role) errors.push("role required");
    else if (!VALID_ROLES.has(role)) errors.push(`unknown role "${role}"`);

    let siteId = null;
    let level = "valid";
    if (siteCode) {
      const site = ctx.sitesByCode.get(siteCode.toLowerCase());
      if (!site) { errors.push(`site code "${siteCode}" not found`); level = "warn"; }
      else siteId = site.id;
    }

    if (errors.some((e) => e === "email required" || e === "invalid email"
                        || e === "role required"  || e.startsWith("unknown role"))) {
      level = "error";
    }

    return {
      level,
      errors,
      normalised: {
        email,
        role,
        client_id: ctx.clientId,
        site_id: siteId,
        is_primary: isPrimary,
      },
    };
  };

  const commitRow = async (n) => {
    const res = await inviteUser(n);
    if (res?.error) return { ok: false, error: res.error.message || "invite failed" };
    return { ok: true };
  };

  if (!hasRole("super_admin") && !hasRole("client_admin")) {
    return <p className="text-sm text-rose-700">Admins only.</p>;
  }

  return (
    <div data-testid="admin-import-users-page">
      {!targetClientId && (
        <div className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3" data-testid="users-import-no-tenant">
          {isSuperAdmin
            ? "Pick a client from the top-bar 'View as client' picker before importing."
            : "No tenant assignment found on your account."}
        </div>
      )}
      <CsvImporter
        title="Import users"
        testid="users-import"
        columns={COLUMNS}
        loadContext={loadContext}
        validateRow={validateRow}
        commitRow={commitRow}
      />
      <p className="text-xs text-slate-500 mt-4">
        Each imported user receives a Supabase magic-link invite to set their password.
      </p>
    </div>
  );
}
